import {
  CANCEL_COMPANY_PLACEHOLDER,
  type CancelSharedDefaults,
  getDefaultCancelPeriodRange,
  getDefaultOperationNotesTemplate,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import type {
  CodAgeCond,
  CodAgeFilterValue,
  ProgrammaticFilterColumn,
  SelectableFilterColumn,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import type { DocumentFilterState } from "@/dashboard/inventory_management/common/portfolioFilterChips";
import type { FilterOptionsByColumn } from "@/dashboard/inventory_management/common/portfolioFilterOptions";
import type {
  FilterOption,
  PortfolioColumnFilters,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import { BeneficiaryTypeEnum, type IndividualCancelStatement } from "@/types";
import { normalizeRegistryCountryRegion } from "@/utils/registries";
import { v4 as uuidv4 } from "uuid";

interface AiStatementLike {
  beneficiary_name?: string;
  registry_code?: string;
  mwh_cancelled?: number;
  beneficiary_type?: string;
  registry_country_region?: string;
  country_region?: string;
  country?: string;
  residency?: string;
  address?: string;
  period_start?: string;
  period_end?: string;
  cancellation_purpose?: string;
}

interface AiCertificateSelection {
  technology_types?: string[];
  production_period_start?: string;
  production_period_end?: string;
  issuing_regions?: string[];
  registry_regions?: string[];
  cod_age?: { a: CodAgeCond; b?: CodAgeCond | null };
  production_support?: boolean;
  ecolabels?: string[];
  production_device_names?: string[];
  portfolio_names?: string[];
}

export interface AiSharedDefaults {
  beneficiary_type?: string;
  period_start?: string;
  period_end?: string;
  cancellation_purpose?: string;
  /**
   * Order-wide privacy preference (DEV-583), set only when the document explicitly asked for it. Unlike
   * the fields above it has no per-statement override, so it is not part of the rehydration below.
   */
  keep_statements_private?: boolean;
}

/**
 * Rehydrate one statement from the backend's shared_defaults wire-compression: order-wide values
 * arrive once in shared_defaults, with per-statement copies kept only as overrides. Absent
 * shared_defaults makes this a no-op.
 */
export const rehydrateStatementFromSharedDefaults = <
  T extends AiSharedDefaults,
>(
  statement: T,
  shared: AiSharedDefaults | undefined
): T =>
  shared
    ? {
        ...statement,
        beneficiary_type: statement.beneficiary_type ?? shared.beneficiary_type,
        period_start: statement.period_start ?? shared.period_start,
        period_end: statement.period_end ?? shared.period_end,
        cancellation_purpose:
          statement.cancellation_purpose ?? shared.cancellation_purpose,
      }
    : statement;

interface AiFormData {
  statements?: AiStatementLike[];
  shared_defaults?: AiSharedDefaults;
  global_operation_notes?: string;
  certificate_selection?: AiCertificateSelection;
}

interface AiResponse {
  form_data?: AiFormData;
}

/** Normalized, document-global certificate-selection criteria (one shared pool per order). */
export interface CertificateSelectionCriteria {
  technologyTypes: string[] | null;
  productionPeriodStart: string | null;
  productionPeriodEnd: string | null;
  issuingRegions: string[] | null;
  registryRegions: string[] | null;
  codAge: CodAgeFilterValue | null;
  productionSupport: boolean | null;
  ecolabels: string[] | null;
  productionDeviceNames: string[] | null;
  portfolioNames: string[] | null;
}

/** Subset of the table's `getFilterOptions()` result the resolver reads for casing recovery. */
export type CertificateFilterOptions = Pick<
  FilterOptionsByColumn,
  | "technology_type"
  | "production_month"
  | "issuing_region"
  | "registry_region"
  | "production_device_name"
  | "portfolio_name"
>;

export interface ResolvedCertificateSelection extends DocumentFilterState {
  /** Per-column filter values, applied to the table as-is (never intersected with stock). */
  filters: PortfolioColumnFilters;
  /** Columns whose requested values have zero matching inventory. */
  unavailable: ProgrammaticFilterColumn[];
}

export interface CancelAiImportResult {
  rows: (IndividualCancelStatement & {
    temp_id: string;
    keep_statement_private: boolean;
  })[];
  sharedDefaults: CancelSharedDefaults;
  globalOperationNotes: string | null;
  hasInconsistentSharedFields: boolean;
  certificateSelection: CertificateSelectionCriteria | null;
}

const parseDateInput = (value?: string): string | null => {
  if (!value?.trim()) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().split("T")[0];
};

const parseBeneficiaryType = (value?: string): BeneficiaryTypeEnum | null => {
  if (
    value === BeneficiaryTypeEnum.CONSUMER ||
    value === BeneficiaryTypeEnum.ELECTRICITY_SELLER
  ) {
    return value;
  }
  return null;
};

const getSharedValueOrNull = <T>(
  values: Array<T | null | undefined>
): T | null => {
  if (
    values.some(
      (value) => value === null || value === undefined || value === ""
    )
  ) {
    return null;
  }
  const uniqueValues = new Set(values as T[]);
  return uniqueValues.size === 1 ? (values[0] as T) : null;
};

// A shared field needs human review when the document provided a value on at
// least one statement but it can't be reduced to a single shared value — because
// the statements disagree, or because some specify it and others omit it. Both
// collapse to a default that silently overrides what the document said. A field
// absent from *every* statement is just a default with nothing to review, so the
// `.some(...)` guard excludes it (and a single statement is excluded by the >1
// gate at the call site).
const needsReview = <T>(values: Array<T | null | undefined>): boolean =>
  values.some(
    (value) => value !== null && value !== undefined && value !== ""
  ) && getSharedValueOrNull(values) === null;

const normalizeStringList = (value?: string[]): string[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  const cleaned = value
    .map((entry) => entry?.trim())
    .filter((entry): entry is string => !!entry);
  return cleaned.length ? cleaned : null;
};

const mapCertificateSelection = (
  raw?: AiCertificateSelection
): CertificateSelectionCriteria | null => {
  if (!raw) {
    return null;
  }
  const criteria: CertificateSelectionCriteria = {
    technologyTypes: normalizeStringList(raw.technology_types),
    productionPeriodStart: raw.production_period_start?.trim() || null,
    productionPeriodEnd: raw.production_period_end?.trim() || null,
    issuingRegions: normalizeStringList(raw.issuing_regions),
    registryRegions: normalizeStringList(raw.registry_regions),
    codAge: raw.cod_age ? { a: raw.cod_age.a, b: raw.cod_age.b ?? null } : null,
    productionSupport:
      typeof raw.production_support === "boolean"
        ? raw.production_support
        : null,
    ecolabels: normalizeStringList(raw.ecolabels),
    productionDeviceNames: normalizeStringList(raw.production_device_names),
    portfolioNames: normalizeStringList(raw.portfolio_names),
  };
  const hasAnyCriterion = Object.values(criteria).some(
    (value) => value !== null
  );
  return hasAnyCriterion ? criteria : null;
};

// Keys are compared after collapsing any run of separators (space/hyphen/underscore) to one space.
const ECOLABEL_SYNONYMS: Record<string, string> = {
  ekoenergy: "ekoenergy",
  "eko energy": "ekoenergy",
  okpower: "okpower-new",
  "ok power": "okpower-new",
  "okpower new": "okpower-new",
  "ok power new": "okpower-new",
};

const normalizeEcolabel = (value: string): string | null =>
  ECOLABEL_SYNONYMS[
    value
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, " ")
  ] ?? null;

const monthToIndex = (month: string): number | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(month.trim());
  if (!match) {
    return null;
  }
  return Number(match[1]) * 12 + (Number(match[2]) - 1);
};

const indexToMonth = (index: number): string => {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
};

/** Expand a `[start, end]` vintage period (YYYY-MM, inclusive) into every month it covers. */
const expandMonthRange = (
  start: string | null,
  end: string | null
): string[] => {
  const startIndex = start ? monthToIndex(start) : null;
  const endIndex = end ? monthToIndex(end) : null;
  if (startIndex !== null && endIndex !== null) {
    if (endIndex < startIndex) {
      return [];
    }
    const months: string[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      months.push(indexToMonth(i));
    }
    return months;
  }
  if (startIndex !== null) {
    return [indexToMonth(startIndex)];
  }
  if (endIndex !== null) {
    return [indexToMonth(endIndex)];
  }
  return [];
};

/**
 * Resolve document criteria into concrete per-column table filter values.
 *
 * Requested values are applied AS-IS (never intersected with stock): an `IN` filter with no matching
 * rows yields an empty table, so auto-select selects nothing and the confirm-gate blocks the order.
 * Inventory options are consulted only to recover exact casing / expand ranges, and to report columns
 * whose requested values have zero matching inventory (so their chips can say so explicitly).
 */
export const resolveCertificateSelectionFilters = (
  criteria: CertificateSelectionCriteria,
  filterOptions: CertificateFilterOptions
): ResolvedCertificateSelection => {
  const result: ResolvedCertificateSelection = {
    filters: {},
    unavailable: [],
  };

  // Empty option lists mean inventory hasn't loaded yet, so we can't judge availability — defer the
  // zero-inventory flag until options exist. The fail-closed sentinels below stay flagged regardless.
  const hasInventoryOptions =
    filterOptions.technology_type.length > 0 ||
    filterOptions.production_month.length > 0 ||
    filterOptions.issuing_region.length > 0 ||
    filterOptions.registry_region.length > 0 ||
    filterOptions.production_device_name.length > 0 ||
    filterOptions.portfolio_name.length > 0;

  const applyWithOptions = (
    column: SelectableFilterColumn,
    requested: string[],
    options: readonly FilterOption[],
    normalize: (value: string) => string | null
  ) => {
    const normalized = requested
      .map(normalize)
      .filter((value): value is string => !!value);
    if (normalized.length === 0) {
      // Nothing normalized (e.g. a region with no supported registry). Fail closed: apply the raw request
      // as an unmatchable sentinel so the table is empty and the confirm-gate blocks, and flag it.
      result.filters[column] = [requested.join(" – ")];
      result.unavailable.push(column);
      return;
    }
    // Only string options can match a requested name (technology's Unknown option is null).
    const byLowerCase = new Map(
      options.flatMap(({ value }): [string, string][] =>
        typeof value === "string" ? [[value.toLowerCase(), value]] : []
      )
    );
    const recovered = [
      ...new Set(
        normalized.map((value) => byLowerCase.get(value.toLowerCase()) ?? value)
      ),
    ];
    result.filters[column] = recovered;
    const optionValues = new Set(options.map((option) => option.value));
    if (
      hasInventoryOptions &&
      !recovered.some((value) => optionValues.has(value))
    ) {
      result.unavailable.push(column);
    }
  };

  if (criteria.technologyTypes?.length) {
    applyWithOptions(
      "technology_type",
      criteria.technologyTypes,
      filterOptions.technology_type,
      (value) => value.trim().toLowerCase() || null
    );
  }

  const {
    productionPeriodStart: vintageStart,
    productionPeriodEnd: vintageEnd,
  } = criteria;
  if (vintageStart || vintageEnd) {
    const months = expandMonthRange(vintageStart, vintageEnd);
    // A present-but-unparseable bound still lets expandMonthRange return the other bound's month, so gate
    // on the bounds themselves, not just months.length.
    const boundsResolve =
      (!vintageStart || monthToIndex(vintageStart) !== null) &&
      (!vintageEnd || monthToIndex(vintageEnd) !== null);
    if (months.length && boundsResolve) {
      applyWithOptions(
        "production_month",
        months,
        filterOptions.production_month,
        (value) => value.trim() || null
      );
    } else {
      // Vintage didn't resolve to a clean YYYY-MM set (unparseable bound or reversed range). Fail closed
      // rather than relax to a wider window (P1): apply the raw request as an unmatchable sentinel so the
      // table is empty and the confirm-gate blocks. It doubles as the chip's unavailable label.
      const requested = [vintageStart, vintageEnd]
        .filter((value): value is string => !!value)
        .join(" – ");
      result.filters.production_month = [requested];
      result.unavailable.push("production_month");
    }
  }

  if (criteria.issuingRegions?.length) {
    applyWithOptions(
      "issuing_region",
      criteria.issuingRegions,
      filterOptions.issuing_region,
      (value) => normalizeRegistryCountryRegion(value)
    );
  }

  if (criteria.registryRegions?.length) {
    applyWithOptions(
      "registry_region",
      criteria.registryRegions,
      filterOptions.registry_region,
      (value) => normalizeRegistryCountryRegion(value)
    );
  }

  if (criteria.codAge) {
    result.filters.cod_age = {
      a: { ...criteria.codAge.a },
      b: criteria.codAge.b ? { ...criteria.codAge.b } : null,
    };
  }

  if (criteria.productionDeviceNames?.length) {
    applyWithOptions(
      "production_device_name",
      criteria.productionDeviceNames,
      filterOptions.production_device_name,
      (value) => value.trim() || null
    );
  }

  if (criteria.portfolioNames?.length) {
    applyWithOptions(
      "portfolio_name",
      criteria.portfolioNames,
      filterOptions.portfolio_name,
      (value) => value.trim() || null
    );
  }

  // Ecolabel and production_support map to fixed value sets whose options don't reflect inventory,
  // so we cannot detect zero-inventory here — the Total pill and shortfall line surface it.
  if (criteria.ecolabels?.length) {
    const ecolabels = [
      ...new Set(
        criteria.ecolabels
          .map(normalizeEcolabel)
          .filter((value): value is string => !!value)
      ),
    ];
    if (ecolabels.length) {
      result.filters.ecolabel_tags = ecolabels;
    } else {
      // No ecolabel matched the synonym map. Fail closed: apply the raw request as an unmatchable
      // sentinel so the table is empty and the confirm-gate blocks, and flag it.
      result.filters.ecolabel_tags = [criteria.ecolabels.join(" – ")];
      result.unavailable.push("ecolabel_tags");
    }
  }

  if (criteria.productionSupport !== null) {
    result.filters.production_support = [criteria.productionSupport];
  }

  return result;
};

export const createEmptyCancelStatement = (): IndividualCancelStatement => {
  return {
    temp_id: uuidv4(),
    beneficiary_name: "",
    beneficiary_type: BeneficiaryTypeEnum.CONSUMER,
    registry_code: null,
    registry_country_region: "",
    address: null,
    period_start: "",
    period_end: "",
    cancellation_purpose: "",
    mwh_cancelled: null,
  };
};

export const mapAiAnalysisToCancelRows = (
  analysisData: AiResponse,
  currentSharedDefaults?: CancelSharedDefaults
): CancelAiImportResult => {
  const fallbackRange = getDefaultCancelPeriodRange();
  const fallbackBeneficiaryType =
    currentSharedDefaults?.beneficiary_type ?? BeneficiaryTypeEnum.CONSUMER;
  const fallbackTemplate =
    currentSharedDefaults?.cancellation_purpose_template ??
    getDefaultOperationNotesTemplate(fallbackBeneficiaryType);

  // After rehydration every downstream path (normalization, shared-default derivation, review
  // flag) runs unchanged.
  const shared = analysisData.form_data?.shared_defaults;
  const statements = (analysisData.form_data?.statements ?? []).map(
    (statement) => rehydrateStatementFromSharedDefaults(statement, shared)
  );
  const normalizedRows = statements.map((statement) => {
    const normalizedRegistryCountry = normalizeRegistryCountryRegion(
      statement.registry_country_region ??
        statement.country_region ??
        statement.country ??
        statement.residency
    );
    const parsedBeneficiaryType = parseBeneficiaryType(
      statement.beneficiary_type
    );

    return {
      beneficiary_name: statement.beneficiary_name?.trim() ?? "",
      registry_code: statement.registry_code?.trim() || null,
      registry_country_region: normalizedRegistryCountry ?? "",
      address: statement.address?.trim() || null,
      mwh_cancelled:
        statement.mwh_cancelled && Number(statement.mwh_cancelled) > 0
          ? Math.floor(Number(statement.mwh_cancelled))
          : null,
      beneficiary_type: parsedBeneficiaryType,
      period_start: parseDateInput(statement.period_start),
      period_end: parseDateInput(statement.period_end),
      cancellation_purpose: statement.cancellation_purpose?.trim() || null,
    };
  });

  const sharedBeneficiaryType = getSharedValueOrNull(
    normalizedRows.map((row) => row.beneficiary_type)
  );
  const sharedPeriodStart = getSharedValueOrNull(
    normalizedRows.map((row) => row.period_start)
  );
  const sharedPeriodEnd = getSharedValueOrNull(
    normalizedRows.map((row) => row.period_end)
  );
  const literalPurposes = normalizedRows.map((row) => row.cancellation_purpose);
  // Keep identical literal text, even if it mentions one of the beneficiaries.
  // Only differing purposes need name substitution to find a shared template.
  const normalizedCancellationPurpose = !needsReview(literalPurposes)
    ? literalPurposes
    : normalizedRows.map((row) => {
        if (!row.cancellation_purpose) return null;
        const name = row.beneficiary_name.trim();
        if (!name) return row.cancellation_purpose;
        return row.cancellation_purpose.replaceAll(
          name,
          CANCEL_COMPANY_PLACEHOLDER
        );
      });
  const sharedCancellationPurpose = getSharedValueOrNull(
    normalizedCancellationPurpose
  );

  const nextSharedDefaults: CancelSharedDefaults = {
    beneficiary_type: sharedBeneficiaryType ?? fallbackBeneficiaryType,
    period_start: sharedPeriodStart ?? fallbackRange.start,
    period_end: sharedPeriodEnd ?? fallbackRange.end,
    cancellation_purpose_template:
      sharedCancellationPurpose ?? fallbackTemplate,
  };

  // DEV-583: the document can only ask for privacy order-wide, so it seeds every row. Each row's own
  // registry still gates whether the flag is submitted, and the user can flip individual rows after.
  const keepStatementPrivate = shared?.keep_statements_private === true;

  const rows = normalizedRows.map((row) => ({
    temp_id: uuidv4(),
    beneficiary_name: row.beneficiary_name,
    beneficiary_type: row.beneficiary_type ?? fallbackBeneficiaryType,
    registry_code: row.registry_code,
    registry_country_region: row.registry_country_region,
    address: row.address,
    period_start: row.period_start ?? fallbackRange.start,
    period_end: row.period_end ?? fallbackRange.end,
    cancellation_purpose: row.cancellation_purpose ?? "",
    mwh_cancelled: row.mwh_cancelled,
    keep_statement_private: keepStatementPrivate,
  }));

  // Flag when a shared field needs review: a genuine cross-statement disagreement,
  // or a value specified on some statements but absent on others (which silently
  // collapses to a default). Gated on >=2 statements — a single statement has
  // nothing to disagree with, and a field absent from every statement is just a
  // default with nothing to review.
  const hasInconsistentSharedFields =
    normalizedRows.length > 1 &&
    (needsReview(normalizedRows.map((row) => row.beneficiary_type)) ||
      needsReview(normalizedRows.map((row) => row.period_start)) ||
      needsReview(normalizedRows.map((row) => row.period_end)) ||
      needsReview(normalizedCancellationPurpose));

  return {
    rows,
    sharedDefaults:
      normalizedRows.length > 0
        ? nextSharedDefaults
        : {
            beneficiary_type: fallbackBeneficiaryType,
            period_start:
              currentSharedDefaults?.period_start ?? fallbackRange.start,
            period_end: currentSharedDefaults?.period_end ?? fallbackRange.end,
            cancellation_purpose_template: fallbackTemplate,
          },
    globalOperationNotes:
      analysisData.form_data?.global_operation_notes?.trim() || null,
    hasInconsistentSharedFields,
    certificateSelection: mapCertificateSelection(
      analysisData.form_data?.certificate_selection
    ),
  };
};
